import { useCallback, useEffect, useRef, useState } from 'react';
import { activityFeedAPI } from 'services/swagger-api';
import {
  LEAD_NOTIFICATION_CATEGORY,
  LeadNotificationDto,
  isLeadNotification
} from './leadNotifications';

/**
 * Filtrage porté par le SERVEUR (category + onlyUnread) : pas de ratissage, un
 * lead non lu ne peut plus passer sous la ligne de flottaison d'un courtier actif.
 */
const PAGE_SIZE = 20;

interface UseUnreadLeadNotificationsReturn {
  leads: LeadNotificationDto[];
  isLoading: boolean;
  /** Retire les leads accusés réception de la liste locale (le back a déjà été appelé). */
  dismiss: (ids: string[]) => void;
}

/**
 * Leads adressés au courtier connecté et non lus. Un seul appel au montage :
 * le composant vit dans le layout, il ne doit pas rejouer la requête à chaque
 * changement de page.
 */
export const useUnreadLeadNotifications = (
  enabled: boolean = true
): UseUnreadLeadNotificationsReturn => {
  const [leads, setLeads] = useState<LeadNotificationDto[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const isMountedRef = useRef(true);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    (async () => {
      try {
        const response = await activityFeedAPI.searchNotificationsAsync({
          searchNotificationsCommand: {
            pageNumber: 1,
            pageSize: PAGE_SIZE,
            category: LEAD_NOTIFICATION_CATEGORY,
            onlyUnread: true
          }
        });
        if (isMountedRef.current) {
          // Garde-fou : une catégorie inconnue est IGNORÉE par le serveur (liste non
          // filtrée). Sans ce re-filtrage, un back pas encore à jour ferait surgir
          // le popup sur n'importe quelle notification non lue.
          setLeads((response.items ?? []).filter(isLeadNotification));
        }
      } catch (error) {
        // Silencieux : un popup d'accueil ne doit jamais parasiter l'entrée dans l'app.
        console.error('Chargement des leads adressés au courtier échoué:', error);
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    })();
  }, [enabled]);

  const dismiss = useCallback((ids: string[]) => {
    const dismissed = new Set(ids);
    setLeads((prev) => prev.filter((lead) => !dismissed.has(lead.id)));
  }, []);

  return { leads, isLoading, dismiss };
};
